import { cloneDeep } from 'es-toolkit';
import moment from 'moment';

export type Timespan<T> = {
  startDate?: string;
  endDate?: string;
  data: T;
};

export function getCurrentTimespan<T>(timeline: Timespan<T>[]): Timespan<T> {
  return getTimespanByDateTime(timeline, undefined);
}

export function getTimespanByDateTime<T>(timeline: Timespan<T>[], date: Date): Timespan<T> {
  if (!timeline) {
    return undefined;
  }
  if (!date) {
    date = new Date();
  }
  const currentTimespans = timeline.filter(
    (timespan) =>
      new Date(timespan.startDate) <= date &&
      (!timespan.endDate || new Date(timespan.endDate) > date),
  );
  if (currentTimespans.length !== 1) {
    return undefined;
  }
  return currentTimespans[0];
}

export function getTimespanByDate<T>(timeline: Timespan<T>[], date: Date): Timespan<T> {
  if (!timeline) {
    return undefined;
  }
  if (!date) {
    date = new Date();
  }
  const currentTimespans = timeline.filter((timespan) => {
    const start = new Date(timespan.startDate);
    start.setUTCHours(0, 0, 0, 0);
    const end = timespan.endDate ? new Date(timespan.endDate) : undefined;
    end?.setUTCHours(23, 59, 59, 999);
    return start <= date && (!timespan.endDate || end > date);
  });
  if (currentTimespans.length !== 1) {
    return undefined;
  }
  return currentTimespans[0];
}

export function timespanToString<T>(timespan: Timespan<T>, translator?: (T) => string): string {
  if (!timespan) {
    return '';
  }
  const start = moment(timespan.startDate);
  const end = timespan.endDate ? moment(timespan.endDate) : undefined;
  const startStr = start.format('DD.MM.YYYY');
  const endStr = end?.format('DD.MM.YYYY') ?? '..........';
  if (translator) {
    return `${startStr} - ${endStr}: ${translator(timespan.data)}`;
  } else {
    return `${startStr} - ${endStr}: ${timespan.data}`;
  }
}

export function timelineToStringRelatedToCurrentDate<T>(
  timeline: Timespan<T>[],
  translator?: (T) => string,
): string {
  if (!timeline) {
    return '';
  }
  const now = new Date();
  const previous = timeline
    .filter((timespan) => timespan.endDate && new Date(timespan.endDate) < now)
    .sort((a, b) => a.startDate?.localeCompare(b.startDate))
    .reverse();
  const future = timeline
    .filter((timespan) => new Date(timespan.startDate) > now)
    .sort((a, b) => a.startDate?.localeCompare(b.startDate))
    .reverse();
  const current = getCurrentTimespan(timeline);
  let str = '';
  if (current) {
    str += 'current:\n' + timespanToString(current, translator);
  }
  if (previous.length > 0) {
    if (str != '') {
      str += '\n\n';
    }
    str +=
      'previous:\n' + previous.map((timespan) => timespanToString(timespan, translator)).join('\n');
  }
  if (future.length > 0) {
    if (str != '') {
      str += '\n\n';
    }
    str +=
      'future:\n' + future.map((timespan) => timespanToString(timespan, translator)).join('\n');
  }
  return str;
}

export function timelineToString(timeline: Timespan<any>[]): string {
  if (!timeline) {
    return '';
  }
  return timeline.map((timespan) => timespanToString(timespan)).join(', ');
}

export function addNewDataToTimeline<T>(
  timeline: Timespan<T>[],
  data: T,
  date?: string,
): Timespan<T>[] {
  timeline = cloneDeep(timeline);
  if (!date) {
    date = new Date().toISOString();
  }
  const newTimespan: Timespan<T> = {
    data,
    startDate: date,
  };
  if (!timeline || timeline.length == 0) {
    return [newTimespan];
  }
  const timespanForThisDate = getTimespanByDateTime(timeline, new Date(date));
  if (timespanForThisDate) {
    newTimespan.endDate = timespanForThisDate.endDate;
    timespanForThisDate.endDate = date;
  } else {
    const minStartDateAfterDate = timeline
      .filter((t) => new Date(t.startDate) > new Date(date))
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .shift();
    if (minStartDateAfterDate) {
      newTimespan.endDate = minStartDateAfterDate.startDate;
    }
  }
  return timeline.concat(newTimespan);
}
